// ═══════════════════════════════════════════════════════════════════════════
// BubbleFeed.jsx — Premium Redesign
// Accepts `embedded` prop when rendered inside the Dashboard sidebar
// (disables fixed height + outer chrome so parent container controls layout)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  Box, Typography, Avatar, List, Paper,
  IconButton, Skeleton, TextField, Button, Snackbar, Alert, Chip,
  Menu, MenuItem, Card, CardContent, CardActions,
  InputAdornment, useMediaQuery, Drawer, Badge,
  ListItemIcon, ListItemText, Tooltip, AvatarGroup,
} from "@mui/material";
import { useTheme, alpha, styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient, useQuery } from "react-query";
import {
  Image as ImageIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  Add as AddIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  AttachMoney as MoneyIcon,
  Campaign as CampaignIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Chat as ChatIcon,
  ArrowForward as ArrowIcon,
  OpenInNew as OpenInNewIcon,
  Menu as MenuIcon,
  Explore as ExploreIcon,
  Link as LinkIcon,
  Bolt as BoltIcon,
  Bubble as BubbleIcon,
} from "@mui/icons-material";

import API from "../API";
import useAuth from "../Hooks/use-auth";
import { useCreatorAuth } from "../Hooks/creator-use-auth";
import CreateBubbleDialog from "./CreateBubbleDialog";
import FeedItem from "../CreatorUser/CreatorPages/Dashboard/zapfeed/components/unitedfeed";
import UploadFileDialog from "../Components/tools/uploadfile";
import CreateCallDialog from "../Pages/Dashboard/Campaigns/castingcalls/CreateCallDialog";
import CastingCallDialog from "../Pages/Dashboard/Campaigns/castingcalls/castingcalldialog";
import ThreadShareDialog from "../CreatorUser/CreatorPages/Dashboard/zapfeed/components/ThreadShareDialog";
import routes from "../Config/routes";

/* ─────────────────────────────────────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────────────────────────────────────── */
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────────────────────────────────────── */

// Outer shell — when embedded, no border/shadow/fixed-height
const ShellContainer = styled(Box, {
  shouldForwardProp: (p) => p !== "embedded",
})(({ theme, embedded }) => ({
  display: "flex",
  overflow: "hidden",
  borderRadius: embedded ? 0 : theme.spacing(2.5),
  border: embedded ? "none" : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
  boxShadow: embedded ? "none" : `0 20px 60px ${alpha(theme.palette.common.black, 0.08)}`,
  background: theme.palette.background.paper,
  ...(embedded
    ? { minHeight: "auto", flexDirection: "column" }
    : {
        height: "auto",
        [theme.breakpoints.up("md")]: { height: "calc(100vh - 200px)" },
        flexDirection: "row",
      }),
}));

// Left sidebar
const SidebarPane = styled(Box, {
  shouldForwardProp: (p) => p !== "embedded",
})(({ theme, embedded }) => ({
  width: embedded ? "100%" : 260,
  flexShrink: 0,
  borderRight: embedded ? "none" : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  borderBottom: embedded ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : "none",
  background: embedded
    ? "transparent"
    : `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${theme.palette.background.paper} 100%)`,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: { display: embedded ? "none" : "none" },
}));

// Main content area
const MainPane = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minWidth: 0,
}));

const ScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: `${alpha(theme.palette.text.primary, 0.1)} transparent`,
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": { borderRadius: 4, background: alpha(theme.palette.text.primary, 0.1) },
}));

// Bubble list item
const BubbleItem = styled(Box, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(1.5),
  cursor: "pointer",
  transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
  background: active
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`
    : "transparent",
  border: active
    ? `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
    : "1px solid transparent",
  "&:hover": {
    background: active
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : alpha(theme.palette.action.hover, 0.6),
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

// Gradient bubble avatar
const BubbleAvatar = styled(Avatar)(({ theme, gradientcolor }) => ({
  width: 38,
  height: 38,
  flexShrink: 0,
  background: gradientcolor
    ? `linear-gradient(135deg, ${gradientcolor} 0%, ${alpha(gradientcolor, 0.6)} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
  fontSize: "0.85rem",
  fontWeight: 700,
}));

// Discovery card
const DiscoveryCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  boxShadow: "none",
  transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  overflow: "hidden",
  animation: `${fadeIn} 0.3s ease`,
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
}));

// Composer wrapper
const ComposerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
  background: alpha(theme.palette.background.default, 0.5),
}));

// Shimmer header for bubble cover
const CoverBox = styled(Box)(({ theme, src }) => ({
  height: 90,
  background: src
    ? `url(${src}) center/cover no-repeat`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  position: "relative",
  "&::after": src
    ? {}
    : {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)",
        backgroundSize: "200% 100%",
        animation: `${shimmer} 3s ease infinite`,
      },
}));

// Section label
const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: theme.palette.text.disabled,
  padding: theme.spacing(1.25, 1.5, 0.5),
}));

// Action button
const ActionBtn = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: theme.spacing(0.6, 1.75),
}));

// Empty state
const EmptyBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6, 3),
  textAlign: "center",
}));

/* ─────────────────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────────────────── */

const getBubbleColor = (bubble) =>
  bubble.theme_colors?.primary || null;

const getBubbleInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────────────────── */

export default function BubbleFeed({ embedded = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { getCurrrentUser, isAuthorized: userOK } = useAuth();
  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();

  // Identity
  const isCreator = !!creatorToken?.creator_user;
  let displayName = "Me";
  let photo;
  if (isCreator) {
    const c = creatorToken.creator_user;
    displayName = c.creator?.display_name || c.username || displayName;
    photo = c.creator?.pfphref || c.pfphref;
  } else if (userOK) {
    const u = getCurrrentUser();
    displayName = u?.first_name || u?.username || displayName;
    photo = u?.phphref;
  }

  // State
  const [view, setView] = useState("feed");
  const [selectedBubble, setSelectedBubble] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [text, setText] = useState("");
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [snack, setSnack] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [castingCallDialogOpen, setCastingCallDialogOpen] = useState(false);
  const [castingCallViewDialogOpen, setCastingCallViewDialogOpen] = useState(false);
  const [selectedCastingCall, setSelectedCastingCall] = useState(null);
  const [castingCallPosterInfo, setCastingCallPosterInfo] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareActivity, setShareActivity] = useState(null);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [shareMenuActivity, setShareMenuActivity] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");

  /* Queries */
  const { data: bubblesData, isLoading: bubblesLoading } = useQuery(
    ["bubbles-list"],
    () => API.bubbles.list(),
    { enabled: userOK || creatorOK, staleTime: 60000, refetchOnWindowFocus: false }
  );

  const myBubbles = bubblesData?.bubbles || [];
  const hasBubbles = myBubbles.length > 0;

  useEffect(() => {
    if (myBubbles.length > 0 && !selectedBubble) setSelectedBubble(myBubbles[0]);
  }, [myBubbles, selectedBubble]);

  const { data: discoverData, isLoading: discoverLoading } = useQuery(
    ["bubbles-discover", searchQuery, sortBy],
    () => API.bubbles.discover({ search: searchQuery, sort_by: sortBy }),
    { enabled: !hasBubbles || view === "discover", staleTime: 30000, refetchOnWindowFocus: false }
  );

  const discoveryBubbles = discoverData?.bubbles || [];

  const {
    data: feedData,
    isLoading: feedLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ["bubble-feed", selectedBubble?.id],
    ({ pageParam = 0 }) =>
      selectedBubble
        ? API.bubbles.getFeed(selectedBubble.id, { page: pageParam, limit: 20 })
        : Promise.resolve({ posts: [], total: 0, has_more: false }),
    {
      enabled: !!selectedBubble && hasBubbles && view === "feed",
      getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  const feed = useMemo(() => {
    return (
      feedData?.pages.flatMap((p) =>
        p.posts.map((post) => ({
          ...post,
          activity_id: post.id,
          activity_name:
            post.activity_type === "casting_call" ? post.casting_call?.brief : post.text,
          activity_type: post.activity_type === "casting_call" ? "CastingCall" : "Post",
          activity_date: post.created_at,
          pfphref: post.pfphref || post.author?.pfphref,
          display_name: post.display_name || post.author?.display_name,
          creator_id: post.creator_id || (post.author?.type === "creator" ? post.author?.username : null),
          user_id: post.user_id || (post.author?.type === "normal" ? post.author?.user_id : null),
          is_global: false,
          bubble_name: selectedBubble?.name,
          can_react: post.activity_type !== "casting_call",
          reaction_counts: post.reaction_counts || {},
          total_reactions: post.total_reactions || 0,
          user_reaction: post.user_reaction,
          reply_count: post.reply_count || 0,
          view_count: post.view_count || 0,
          ...(post.casting_call && { casting_call: post.casting_call }),
        }))
      ) ?? []
    );
  }, [feedData, selectedBubble]);

  /* Mutations */
  const createPost = useMutation(
    async ({ bubbleId, text, mediaData }) => {
      const d = { text: text || "", bubble_id: bubbleId };
      if (mediaData) { d.media_url = mediaData.url; d.media_type = mediaData.type; }
      return API.zapfeed.post(d);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(["bubble-feed", selectedBubble?.id]);
        setText(""); setUploadedMedia(null);
        setSnack({ msg: "Posted to bubble!", sev: "success" });
      },
      onError: (err) => setSnack({ msg: err?.response?.data?.error || "Failed to post", sev: "error" }),
    }
  );

  const joinMutation = useMutation(
    (bubbleId) => API.bubbles.join(bubbleId),
    {
      onSuccess: (_, bubbleId) => {
        qc.invalidateQueries(["bubbles-list"]);
        qc.invalidateQueries(["bubbles-discover"]);
        qc.refetchQueries(["bubbles-list"]).then(() => {
          const updated = qc.getQueryData(["bubbles-list"]);
          const joined = updated?.bubbles?.find((b) => b.id === bubbleId);
          if (joined) { setSelectedBubble(joined); setView("feed"); }
        });
        setSnack({ msg: "Joined! Welcome to the bubble.", sev: "success" });
      },
      onError: (err) => setSnack({ msg: err?.response?.data?.error || "Failed to join", sev: "error" }),
    }
  );

  /* Handlers */
  const handleBubbleSelect = (bubble) => {
    setSelectedBubble(bubble);
    setView("feed");
    if (isMobile) setSidebarOpen(false);
  };

  const handleSubmit = () => {
    const t = text.trim();
    if (!t && !uploadedMedia) return;
    if (t && t.length > 280) return;
    if (!selectedBubble) { setSnack({ msg: "Select a bubble first", sev: "warning" }); return; }
    createPost.mutate({ bubbleId: selectedBubble.id, text: t, mediaData: uploadedMedia });
  };

  const handleCastingCallSuccess = useCallback(() => {
    setCastingCallDialogOpen(false);
    if (selectedBubble) qc.invalidateQueries(["bubble-feed", selectedBubble?.id]);
    setSnack({ msg: "Casting call created!", sev: "success" });
  }, [selectedBubble, qc]);

  const handleCastingCallClick = (activity) => {
    if (activity.casting_call) {
      setSelectedCastingCall(activity.casting_call);
      setCastingCallPosterInfo({ pfphref: activity.pfphref, display_name: activity.display_name });
      setCastingCallViewDialogOpen(true);
    }
  };

  const handleCastingCallShareClick = (e, activity) => {
    e.stopPropagation();
    setShareMenuActivity(activity);
    setShareMenuAnchor(e.currentTarget);
  };

  const handleShareMenuClose = () => { setShareMenuAnchor(null); setShareMenuActivity(null); };

  const handleCopyPublicLink = async () => {
    if (shareMenuActivity?.casting_call?.public_slug) {
      try {
        await navigator.clipboard.writeText(`${window.location.origin}/casting-calls/public/${shareMenuActivity.casting_call.public_slug}`);
        setSnack({ msg: "Link copied!", sev: "success" });
      } catch { setSnack({ msg: "Failed to copy", sev: "error" }); }
    }
    handleShareMenuClose();
  };

  const handleOpenInNewTab = () => {
    if (shareMenuActivity?.casting_call?.public_slug)
      window.open(`/casting-calls/public/${shareMenuActivity.casting_call.public_slug}`, "_blank");
    handleShareMenuClose();
  };

  /* Infinite scroll */
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage || view !== "feed") return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && fetchNextPage(),
      { rootMargin: "400px 0px 0px 0px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage, view]);

  /* ─── Loading ─────────────────────────────────────────────── */
  if (bubblesLoading) {
    return (
      <Box sx={{ p: embedded ? 1.5 : 3 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Box key={i} sx={{ mb: 1.5, display: "flex", gap: 1.5, alignItems: "center" }}>
            <Skeleton variant="circular" width={38} height={38} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={14} />
              <Skeleton width="40%" height={11} sx={{ mt: 0.4 }} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  /* ─── Sidebar content (shared between drawer + desktop) ───── */
  const SidebarContent = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header actions */}
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
        <ActionBtn
          fullWidth
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => { setCreateDialogOpen(true); if (isMobile) setSidebarOpen(false); }}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          Create Bubble
        </ActionBtn>
        <ActionBtn
          fullWidth
          variant={view === "discover" ? "contained" : "outlined"}
          startIcon={<ExploreIcon sx={{ fontSize: 16 }} />}
          onClick={() => { setView("discover"); if (isMobile) setSidebarOpen(false); }}
          sx={
            view === "discover"
              ? { bgcolor: alpha(theme.palette.primary.main, 0.12), color: "primary.main", border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }
              : { borderColor: alpha(theme.palette.divider, 0.8), color: "text.secondary" }
          }
        >
          Discover
        </ActionBtn>
      </Box>

      {/* Bubble list */}
      <ScrollArea sx={{ flex: 1, px: 0.75 }}>
        {myBubbles.length > 0 && (
          <>
            <SectionLabel>My Bubbles ({myBubbles.length})</SectionLabel>
            {myBubbles.map((bubble) => {
              const color = getBubbleColor(bubble);
              const isActive = selectedBubble?.id === bubble.id && view === "feed";
              return (
                <BubbleItem key={bubble.id} active={isActive} onClick={() => handleBubbleSelect(bubble)}>
                  {bubble.avatar_image ? (
                    <Avatar src={bubble.avatar_image} sx={{ width: 38, height: 38, flexShrink: 0 }} />
                  ) : (
                    <BubbleAvatar gradientcolor={color}>
                      {getBubbleInitials(bubble.name)}
                    </BubbleAvatar>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 700 : 600}
                      noWrap
                      sx={{ fontSize: "0.8rem", color: isActive ? "primary.main" : "text.primary" }}
                    >
                      {bubble.name}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.67rem" }}>
                      {bubble.member_count} member{bubble.member_count !== 1 ? "s" : ""}
                      {bubble.recent_activity > 0 && ` · ${bubble.recent_activity} posts`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.25, opacity: 0.7 }}>
                    {bubble.has_chat && (
                      <Tooltip title="Open chat">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${routes.conversationshub}?conversation=${bubble.conversation_id}`);
                          }}
                          sx={{ p: 0.4 }}
                        >
                          <ChatIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Open full page">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/bubbles/${bubble.id}`); }}
                        sx={{ p: 0.4 }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </BubbleItem>
              );
            })}
          </>
        )}
      </ScrollArea>
    </Box>
  );

  /* ─── Discover grid ───────────────────────────────────────── */
  const DiscoverGrid = () => (
    <Box sx={{ p: embedded ? 1 : 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search communities…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} /></InputAdornment>,
          sx: { borderRadius: 2.5, fontSize: "0.85rem" },
        }}
        sx={{ mb: 1.5 }}
      />

      {/* Sort chips */}
      <Box sx={{ display: "flex", gap: 0.75, mb: 2 }}>
        {[
          { val: "recent", label: "Recent", icon: <ScheduleIcon sx={{ fontSize: 13 }} /> },
          { val: "popular", label: "Popular", icon: <TrendingIcon sx={{ fontSize: 13 }} /> },
          { val: "members", label: "Members", icon: <PeopleIcon sx={{ fontSize: 13 }} /> },
        ].map(({ val, label, icon }) => (
          <Chip
            key={val}
            label={label}
            icon={icon}
            size="small"
            onClick={() => setSortBy(val)}
            variant={sortBy === val ? "filled" : "outlined"}
            color={sortBy === val ? "primary" : "default"}
            sx={{ borderRadius: 20, fontWeight: 600, fontSize: "0.72rem", cursor: "pointer" }}
          />
        ))}
      </Box>

      {discoverLoading ? (
        <Box sx={{ display: "grid", gridTemplateColumns: embedded ? "1fr" : "1fr 1fr", gap: 1.5 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Box key={i}>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
              <Box sx={{ pt: 1 }}>
                <Skeleton width="70%" height={14} />
                <Skeleton width="50%" height={11} sx={{ mt: 0.4 }} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : discoveryBubbles.length === 0 ? (
        <EmptyBox>
          <ExploreIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? "No results found" : "Nothing to discover yet"}
          </Typography>
        </EmptyBox>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: embedded ? "1fr" : "1fr 1fr", gap: 1.5 }}>
          {discoveryBubbles.map((bubble) => (
            <DiscoveryCard key={bubble.id} onClick={() => navigate(`/bubbles/${bubble.id}`)}>
              <CoverBox src={bubble.cover_image}>
                {bubble.avatar_image && (
                  <Avatar
                    src={bubble.avatar_image}
                    sx={{
                      width: 44, height: 44,
                      border: `2px solid ${theme.palette.background.paper}`,
                      position: "absolute", bottom: -22, left: 14,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
                    }}
                  />
                )}
              </CoverBox>

              <CardContent sx={{ pt: bubble.avatar_image ? 3.5 : 1.5, pb: 1, px: 1.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                  {!bubble.avatar_image && (
                    <BubbleAvatar gradientcolor={getBubbleColor(bubble)} sx={{ width: 28, height: 28, fontSize: "0.65rem" }}>
                      {getBubbleInitials(bubble.name)}
                    </BubbleAvatar>
                  )}
                  <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                    {bubble.name}
                  </Typography>
                  {bubble.is_public ? (
                    <PublicIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  ) : (
                    <LockIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  )}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.45,
                    fontSize: "0.72rem",
                    minHeight: 32,
                  }}
                >
                  {bubble.description || "No description"}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
                  <Chip
                    size="small"
                    label={`${bubble.member_count} members`}
                    sx={{ height: 18, fontSize: "0.62rem" }}
                  />
                  {bubble.has_chat && (
                    <Chip size="small" label="Chat" color="info" variant="outlined" sx={{ height: 18, fontSize: "0.62rem" }} />
                  )}
                  {bubble.is_paid && (
                    <Chip
                      size="small"
                      label={bubble.is_subscription ? `$${bubble.access_price}/mo` : `$${bubble.access_price}`}
                      color="primary"
                      sx={{ height: 18, fontSize: "0.62rem" }}
                    />
                  )}
                </Box>
              </CardContent>

              <CardActions sx={{ px: 1.75, pb: 1.5, pt: 0, gap: 0.75 }}>
                {bubble.is_paid ? (
                  <ActionBtn
                    fullWidth variant="contained" size="small"
                    onClick={(e) => { e.stopPropagation(); navigate(`/bubbles/${bubble.id}`); }}
                    endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                  >
                    View Details
                  </ActionBtn>
                ) : !bubble.is_member ? (
                  <>
                    <ActionBtn
                      variant="outlined" size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/bubbles/${bubble.id}`); }}
                      sx={{ flex: 1, borderColor: alpha(theme.palette.divider, 0.8) }}
                    >
                      View
                    </ActionBtn>
                    <ActionBtn
                      variant="contained" size="small"
                      onClick={(e) => { e.stopPropagation(); joinMutation.mutate(bubble.id); }}
                      disabled={joinMutation.isLoading}
                      sx={{ flex: 1 }}
                    >
                      {joinMutation.isLoading ? "Joining…" : "Join"}
                    </ActionBtn>
                  </>
                ) : (
                  <ActionBtn
                    fullWidth variant="outlined" size="small"
                    onClick={(e) => { e.stopPropagation(); navigate(`/bubbles/${bubble.id}`); }}
                    endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                    sx={{ borderColor: alpha(theme.palette.primary.main, 0.4), color: "primary.main" }}
                  >
                    Open Community
                  </ActionBtn>
                )}
              </CardActions>
            </DiscoveryCard>
          ))}
        </Box>
      )}

      {/* Create a bubble CTA */}
      <Box
        sx={{
          mt: 2.5,
          p: 2.5,
          borderRadius: 2.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
          textAlign: "center",
        }}
      >
        <GroupsIcon sx={{ fontSize: 32, color: alpha(theme.palette.primary.main, 0.5), mb: 1 }} />
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Start your own community
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Create a bubble, invite your people, build something great
        </Typography>
        <ActionBtn
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Bubble
        </ActionBtn>
      </Box>
    </Box>
  );

  /* ─── No bubbles: show discover only ────────────────────────── */
  if (!hasBubbles) {
    return (
      <Box sx={{ p: embedded ? 0 : 2 }}>
        {!embedded && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Discover Communities</Typography>
            <Typography variant="body2" color="text.secondary">Find your people on Blitz</Typography>
          </Box>
        )}
        <DiscoverGrid />

        <CreateBubbleDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={() => {
            qc.invalidateQueries(["bubbles-list"]);
            setSnack({ msg: "Bubble created!", sev: "success" });
            setCreateDialogOpen(false);
          }}
          API={API}
        />

        <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          {snack && <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>{snack.msg}</Alert>}
        </Snackbar>
      </Box>
    );
  }

  /* ─── Full feed + sidebar layout ────────────────────────────── */

  // When embedded in sidebar, collapse the left pane and just show feed/discover
  if (embedded) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* Compact embedded bubble nav */}
        <Box
          sx={{
            px: 1.25,
            pt: 1,
            pb: 0.75,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <ActionBtn
              variant="outlined"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ flex: 1, fontSize: "0.7rem", py: 0.5, borderColor: alpha(theme.palette.divider, 0.7), color: "text.secondary" }}
            >
              Create
            </ActionBtn>
            <ActionBtn
              variant={view === "discover" ? "contained" : "outlined"}
              size="small"
              startIcon={<ExploreIcon sx={{ fontSize: 13 }} />}
              onClick={() => setView(view === "discover" ? "feed" : "discover")}
              sx={{ flex: 1, fontSize: "0.7rem", py: 0.5, ...(view !== "discover" && { borderColor: alpha(theme.palette.divider, 0.7), color: "text.secondary" }) }}
            >
              Discover
            </ActionBtn>
          </Box>

          {/* Bubble selector row */}
          {view === "feed" && myBubbles.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.75, overflowX: "auto", pb: 0.5, "&::-webkit-scrollbar": { display: "none" } }}>
              {myBubbles.map((bubble) => {
                const isActive = selectedBubble?.id === bubble.id;
                const color = getBubbleColor(bubble);
                return (
                  <Chip
                    key={bubble.id}
                    label={bubble.name}
                    avatar={
                      bubble.avatar_image ? (
                        <Avatar src={bubble.avatar_image} />
                      ) : (
                        <Avatar sx={{ background: color ? `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.6)} 100%)` : undefined }}>
                          {getBubbleInitials(bubble.name)}
                        </Avatar>
                      )
                    }
                    size="small"
                    onClick={() => handleBubbleSelect(bubble)}
                    variant={isActive ? "filled" : "outlined"}
                    color={isActive ? "primary" : "default"}
                    sx={{ fontWeight: 600, fontSize: "0.68rem", cursor: "pointer", flexShrink: 0 }}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        {/* Content */}
        {view === "discover" ? (
          <DiscoverGrid />
        ) : selectedBubble ? (
          <>
            {/* Mini composer */}
            <ComposerBox>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Avatar src={photo} sx={{ width: 26, height: 26, mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    multiline minRows={1} maxRows={4} fullWidth
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={`Post in ${selectedBubble.name}…`}
                    variant="standard"
                    InputProps={{ disableUnderline: true, sx: { fontSize: "0.78rem" } }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                    <Box sx={{ display: "flex", gap: 0.25 }}>
                      <IconButton size="small" onClick={() => setUploadDialogOpen(true)} disabled={!!uploadedMedia} sx={{ p: 0.35, color: "primary.main" }}>
                        <ImageIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setCastingCallDialogOpen(true)} sx={{ p: 0.35, color: "secondary.main" }}>
                        <CampaignIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Box>
                    <Button
                      size="small" variant="contained"
                      onClick={handleSubmit}
                      disabled={(!text.trim() && !uploadedMedia) || createPost.isLoading}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontSize: "0.68rem", px: 1.5, py: 0.3, minWidth: "auto" }}
                    >
                      Post
                    </Button>
                  </Box>
                </Box>
              </Box>
            </ComposerBox>

            {/* Feed */}
            {feedLoading ? (
              <Box sx={{ p: 1 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Box key={i} sx={{ mb: 1.5, p: 1.25, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <Skeleton variant="circular" width={30} height={30} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="50%" height={13} />
                        <Skeleton width="30%" height={11} sx={{ mt: 0.3 }} />
                      </Box>
                    </Box>
                    <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
                  </Box>
                ))}
              </Box>
            ) : feed.length === 0 ? (
              <EmptyBox sx={{ py: 4 }}>
                <BoltIcon sx={{ fontSize: 32, color: alpha(theme.palette.primary.main, 0.3), mb: 1 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>No posts yet</Typography>
                <Typography variant="caption" color="text.disabled">Be the first to post!</Typography>
              </EmptyBox>
            ) : (
              <List sx={{ p: 0 }}>
                {feed.map((post) => (
                  <FeedItem
                    key={post.id}
                    activity={post}
                    userAuth={{ userOK, creatorOK }}
                    queryClient={qc}
                    onSnackbarMessage={(msg, sev) => setSnack({ msg, sev })}
                    isCreator={isCreator}
                    onCastingCallClick={handleCastingCallClick}
                    onCastingCallShareClick={handleCastingCallShareClick}
                    onShare={(a) => { setShareActivity(a); setShareDialogOpen(true); }}
                  />
                ))}
                <Box ref={sentinelRef} sx={{ height: 1 }} />
                {isFetchingNextPage && (
                  <Box sx={{ py: 1.5, textAlign: "center" }}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mx: 1.25 }} />
                  </Box>
                )}
              </List>
            )}
          </>
        ) : null}

        {/* Shared dialogs */}
        <SharedDialogs
          createDialogOpen={createDialogOpen} setCreateDialogOpen={setCreateDialogOpen}
          uploadDialogOpen={uploadDialogOpen} setUploadDialogOpen={setUploadDialogOpen}
          setUploadedMedia={setUploadedMedia} setSnack={setSnack}
          castingCallDialogOpen={castingCallDialogOpen} setCastingCallDialogOpen={setCastingCallDialogOpen}
          handleCastingCallSuccess={handleCastingCallSuccess}
          castingCallViewDialogOpen={castingCallViewDialogOpen} setCastingCallViewDialogOpen={setCastingCallViewDialogOpen}
          selectedCastingCall={selectedCastingCall} setSelectedCastingCall={setSelectedCastingCall}
          castingCallPosterInfo={castingCallPosterInfo} setCastingCallPosterInfo={setCastingCallPosterInfo}
          shareDialogOpen={shareDialogOpen} setShareDialogOpen={setShareDialogOpen}
          shareActivity={shareActivity} setShareActivity={setShareActivity}
          shareMenuAnchor={shareMenuAnchor} shareMenuActivity={shareMenuActivity}
          handleShareMenuClose={handleShareMenuClose} handleCopyPublicLink={handleCopyPublicLink}
          handleOpenInNewTab={handleOpenInNewTab}
          selectedBubble={selectedBubble} qc={qc} snack={snack} setSnack={setSnack}
        />
      </Box>
    );
  }

  /* ─── Full-page / standalone layout ─────────────────────────── */
  return (
    <ShellContainer embedded={false}>
      {/* Desktop sidebar */}
      {!isMobile && (
        <SidebarPane embedded={false}>
          <SidebarContent />
        </SidebarPane>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} sx={{ "& .MuiDrawer-paper": { width: 260 } }}>
          <SidebarContent />
        </Drawer>
      )}

      {/* Main content */}
      <MainPane>
        {/* Top bar */}
        <Box
          sx={{
            px: 2.5, py: 1.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            display: "flex", alignItems: "center", gap: 2,
            background: alpha(theme.palette.background.default, 0.7),
            backdropFilter: "blur(8px)",
            flexShrink: 0,
          }}
        >
          {isMobile && (
            <IconButton onClick={() => setSidebarOpen(true)} size="small">
              <Badge badgeContent={myBubbles.length} color="primary" max={9}>
                <MenuIcon />
              </Badge>
            </IconButton>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {view === "discover"
                ? "Discover Communities"
                : selectedBubble
                ? selectedBubble.name
                : "Your Bubbles"}
            </Typography>
            {view === "feed" && selectedBubble && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {selectedBubble.member_count} member{selectedBubble.member_count !== 1 ? "s" : ""}
                {selectedBubble.is_public ? " · Public" : " · Private"}
              </Typography>
            )}
          </Box>

          {view === "feed" && selectedBubble && (
            <Tooltip title="Open full page">
              <IconButton size="small" onClick={() => navigate(`/bubbles/${selectedBubble.id}`)}>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Feed / Discover content */}
        <ScrollArea>
          {view === "discover" ? (
            <DiscoverGrid />
          ) : selectedBubble ? (
            <Box>
              {/* Post composer */}
              <ComposerBox>
                <Box sx={{ display: "flex", gap: 1.25 }}>
                  <Avatar src={photo} sx={{ width: 36, height: 36, mt: 0.25 }} />
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      multiline minRows={2} maxRows={6} fullWidth
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={`What's happening in ${selectedBubble.name}?`}
                      variant="standard"
                      InputProps={{ disableUnderline: true, sx: { fontSize: "0.875rem" } }}
                    />
                    {uploadedMedia && (
                      <Box sx={{ position: "relative", mt: 1, maxWidth: 240 }}>
                        <Box component="img" src={uploadedMedia.url} sx={{ width: "100%", borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.divider, 0.4)}`, maxHeight: 140, objectFit: "cover" }} />
                        <IconButton size="small" onClick={() => setUploadedMedia(null)} sx={{ position: "absolute", top: 6, right: 6, bgcolor: alpha(theme.palette.background.paper, 0.9), p: 0.3 }}>
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Add image">
                          <span>
                            <IconButton size="small" onClick={() => setUploadDialogOpen(true)} disabled={!!uploadedMedia} sx={{ color: "primary.main" }}>
                              <ImageIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Casting call">
                          <IconButton size="small" onClick={() => setCastingCallDialogOpen(true)} sx={{ color: "secondary.main" }}>
                            <CampaignIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Button
                        variant="contained" size="small"
                        onClick={handleSubmit}
                        disabled={(!text.trim() && !uploadedMedia) || text.length > 280 || createPost.isLoading}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 2.5 }}
                      >
                        Post
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </ComposerBox>

              {/* Feed posts */}
              {feedLoading ? (
                <Box sx={{ p: 2 }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Box key={i} sx={{ mb: 2, p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
                      <Box sx={{ display: "flex", gap: 1.25, mb: 1.25 }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="45%" height={14} />
                          <Skeleton width="25%" height={11} sx={{ mt: 0.4 }} />
                        </Box>
                      </Box>
                      <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1.5 }} />
                    </Box>
                  ))}
                </Box>
              ) : feed.length === 0 ? (
                <EmptyBox>
                  <BoltIcon sx={{ fontSize: 52, color: alpha(theme.palette.primary.main, 0.2), mb: 2 }} />
                  <Typography variant="h6" fontWeight={700} color="text.secondary" gutterBottom>
                    No posts yet
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Be the first to share something in {selectedBubble.name}
                  </Typography>
                </EmptyBox>
              ) : (
                <List sx={{ p: 0 }}>
                  {feed.map((post) => (
                    <FeedItem
                      key={post.id}
                      activity={post}
                      userAuth={{ userOK, creatorOK }}
                      queryClient={qc}
                      onSnackbarMessage={(msg, sev) => setSnack({ msg, sev })}
                      isCreator={isCreator}
                      onCastingCallClick={handleCastingCallClick}
                      onCastingCallShareClick={handleCastingCallShareClick}
                      onShare={(a) => { setShareActivity(a); setShareDialogOpen(true); }}
                    />
                  ))}
                  <Box ref={sentinelRef} sx={{ height: 2 }} />
                  {isFetchingNextPage && (
                    <Box sx={{ py: 2, px: 2 }}>
                      <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 2 }} />
                    </Box>
                  )}
                  <Box sx={{ height: 20 }} />
                </List>
              )}
            </Box>
          ) : null}
        </ScrollArea>
      </MainPane>

      {/* Shared dialogs */}
      <SharedDialogs
        createDialogOpen={createDialogOpen} setCreateDialogOpen={setCreateDialogOpen}
        uploadDialogOpen={uploadDialogOpen} setUploadDialogOpen={setUploadDialogOpen}
        setUploadedMedia={setUploadedMedia} setSnack={setSnack}
        castingCallDialogOpen={castingCallDialogOpen} setCastingCallDialogOpen={setCastingCallDialogOpen}
        handleCastingCallSuccess={handleCastingCallSuccess}
        castingCallViewDialogOpen={castingCallViewDialogOpen} setCastingCallViewDialogOpen={setCastingCallViewDialogOpen}
        selectedCastingCall={selectedCastingCall} setSelectedCastingCall={setSelectedCastingCall}
        castingCallPosterInfo={castingCallPosterInfo} setCastingCallPosterInfo={setCastingCallPosterInfo}
        shareDialogOpen={shareDialogOpen} setShareDialogOpen={setShareDialogOpen}
        shareActivity={shareActivity} setShareActivity={setShareActivity}
        shareMenuAnchor={shareMenuAnchor} shareMenuActivity={shareMenuActivity}
        handleShareMenuClose={handleShareMenuClose} handleCopyPublicLink={handleCopyPublicLink}
        handleOpenInNewTab={handleOpenInNewTab}
        selectedBubble={selectedBubble} qc={qc} snack={snack} setSnack={setSnack}
      />
    </ShellContainer>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Extracted shared dialogs (avoids duplication between embedded + full modes)
───────────────────────────────────────────────────────────────────────────── */
function SharedDialogs({
  createDialogOpen, setCreateDialogOpen,
  uploadDialogOpen, setUploadDialogOpen, setUploadedMedia, setSnack,
  castingCallDialogOpen, setCastingCallDialogOpen, handleCastingCallSuccess,
  castingCallViewDialogOpen, setCastingCallViewDialogOpen,
  selectedCastingCall, setSelectedCastingCall,
  castingCallPosterInfo, setCastingCallPosterInfo,
  shareDialogOpen, setShareDialogOpen, shareActivity, setShareActivity,
  shareMenuAnchor, shareMenuActivity, handleShareMenuClose,
  handleCopyPublicLink, handleOpenInNewTab,
  selectedBubble, qc, snack,
}) {
  return (
    <>
      <CreateBubbleDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          qc.invalidateQueries(["bubbles-list"]);
          setSnack({ msg: "Bubble created!", sev: "success" });
          setCreateDialogOpen(false);
        }}
        API={API}
      />

      <UploadFileDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUploadSuccess={(fileData) => {
          setUploadedMedia({
            url: fileData.url,
            type: fileData.file_type === "gif" ? "gif" : "image",
          });
          setSnack({ msg: "Image uploaded!", sev: "success" });
        }}
      />

      <CreateCallDialog
        open={castingCallDialogOpen}
        handleClose={() => setCastingCallDialogOpen(false)}
        onSuccess={handleCastingCallSuccess}
        mode="bubble"
        bubbleId={selectedBubble?.id}
        bubbleName={selectedBubble?.name}
        bubbleThemeColors={selectedBubble?.theme_colors}
        conversationId={selectedBubble?.conversation_id}
        defaultScopeType="bubble"
        autoSelectBubble={true}
      />

      <CastingCallDialog
        open={castingCallViewDialogOpen}
        onClose={() => {
          setCastingCallViewDialogOpen(false);
          setSelectedCastingCall(null);
          setCastingCallPosterInfo(null);
        }}
        castingCall={selectedCastingCall}
        posterInfo={castingCallPosterInfo}
      />

      {shareActivity && (
        <ThreadShareDialog
          open={shareDialogOpen}
          onClose={() => { setShareDialogOpen(false); setShareActivity(null); }}
          activity={shareActivity}
        />
      )}

      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: `0 8px 24px ${alpha("#000", 0.12)}` } }}
      >
        <MenuItem onClick={handleCopyPublicLink}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Copy public link" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
        </MenuItem>
        <MenuItem onClick={handleOpenInNewTab}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Open in new tab" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack && (
          <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}